class WithdrawalFormController {
  constructor(form) {
    this.form = form;
    this.detailsStep = form.querySelector('[data-withdrawal-step="details"]');
    this.reviewStep = form.querySelector('[data-withdrawal-step="review"]');
    this.reviewButton = form.querySelector('[data-withdrawal-review]');
    this.backButton = form.querySelector('[data-withdrawal-back]');
    this.submitButton = form.querySelector('[data-withdrawal-submit]');
    this.reviewHeading = form.querySelector('[data-withdrawal-review-heading]');
    this.timestampInput = form.querySelector('[data-withdrawal-timestamp]');
    this.statementInput = form.querySelector('[data-withdrawal-statement]');
    this.status = form.querySelector('[data-withdrawal-status]');
    this.isSubmitting = false;
  }

  init() {
    if (!this.detailsStep || !this.reviewStep || !this.reviewButton || !this.backButton) return;

    this.reviewButton.addEventListener('click', () => this.showReview());
    this.backButton.addEventListener('click', () => this.showDetails());
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  getFieldValue(name) {
    const field = this.form.querySelector(`[data-withdrawal-field="${name}"]`);
    return field ? field.value.trim() : '';
  }

  createReceipt() {
    const now = new Date();
    const locale = document.documentElement.lang || 'de';

    return {
      name: this.getFieldValue('name'),
      email: this.getFieldValue('email'),
      order: this.getFieldValue('order'),
      items: this.getFieldValue('items'),
      notes: this.getFieldValue('notes'),
      statement: this.statementInput ? this.statementInput.value : '',
      submittedAt: new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'long'
      }).format(now),
      submittedAtIso: now.toISOString()
    };
  }

  renderReview(receipt) {
    Object.entries(receipt).forEach(([key, value]) => {
      const target = this.form.querySelector(`[data-withdrawal-review-value="${key}"]`);
      if (target) target.textContent = value || '—';
    });
  }

  showReview() {
    if (!this.form.reportValidity()) return;

    const receipt = this.createReceipt();
    this.renderReview(receipt);
    this.timestampInput.value = receipt.submittedAtIso;
    this.detailsStep.hidden = true;
    this.reviewStep.hidden = false;
    this.status.textContent = this.reviewHeading.textContent;
    this.reviewHeading.focus();
  }

  showDetails() {
    this.reviewStep.hidden = true;
    this.detailsStep.hidden = false;
    this.status.textContent = '';
    this.reviewButton.focus();
  }

  handleSubmit(event) {
    if (this.reviewStep.hidden) {
      event.preventDefault();
      this.showReview();
      return;
    }

    if (this.isSubmitting) {
      event.preventDefault();
      return;
    }

    this.isSubmitting = true;
    const receipt = this.createReceipt();
    this.timestampInput.value = receipt.submittedAtIso;
    const serializedReceipt = JSON.stringify(receipt);

    try {
      sessionStorage.setItem('mostik:withdrawal-receipt', serializedReceipt);
    } catch (error) {
      try {
        // window.name survives a same-origin redirect and is cleared after the receipt is rendered.
        window.name = `mostik:withdrawal-receipt:${serializedReceipt}`;
      } catch (fallbackError) {
        // The contact request can still submit when both browser storage mechanisms are unavailable.
      }
    }

    if (this.submitButton) {
      this.submitButton.classList.add('is-loading');
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.disabled = true;
    }
  }
}

class ShopifyWithdrawalFormController {
  constructor(container) {
    this.container = container;
    this.embed = null;
    this.shadowRoot = null;
    this.observer = null;
    this.containerObserver = null;
    this.retryTimer = null;
    this.applyQueued = false;
    this.orderInputs = new WeakSet();
    this.forms = new WeakSet();
    this.labelText = {
      first_name: container.dataset.formLabelFirstName,
      email: container.dataset.formLabelEmail,
      last_name: container.dataset.formLabelLastName,
      'custom#order_or_contract_id': container.dataset.formLabelOrder,
      'custom#withdrawal_scope_titles_and_quantities': container.dataset.formLabelItems,
      'custom#note': container.dataset.formLabelNotes
    };

    this.handleOrderInput = this.handleOrderInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.queueApply = this.queueApply.bind(this);
  }

  init() {
    this.findEmbed();

    if (!this.embed) {
      this.containerObserver = new MutationObserver(() => this.findEmbed());
      this.containerObserver.observe(this.container, { childList: true, subtree: true });
    }

    window.addEventListener('resize', this.queueApply, { passive: true });
  }

  findEmbed() {
    const embed = this.container.querySelector('shopify-forms-embed');
    if (!embed || embed === this.embed) return;

    this.embed = embed;
    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }
    this.connectShadowRoot();
  }

  connectShadowRoot() {
    if (!this.embed) return;

    const shadowRoot = this.embed.shadowRoot;
    if (!shadowRoot) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = window.setTimeout(() => this.connectShadowRoot(), 100);
      return;
    }

    if (shadowRoot === this.shadowRoot) return;

    this.shadowRoot = shadowRoot;
    this.applyTranslationsAndValidation();
    this.observer = new MutationObserver(this.queueApply);
    this.observer.observe(shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  queueApply() {
    if (this.applyQueued || !this.shadowRoot) return;

    this.applyQueued = true;
    window.requestAnimationFrame(() => {
      this.applyQueued = false;
      this.applyTranslationsAndValidation();
    });
  }

  applyTranslationsAndValidation() {
    if (!this.shadowRoot) return;

    Object.entries(this.labelText).forEach(([fieldId, text]) => {
      if (!text) return;

      const label = this.shadowRoot.querySelector(`label[for="${fieldId}"]`);
      if (label && label.textContent.trim() !== text) {
        const visibleTextNode = Array.from(label.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
        );

        if (visibleTextNode) {
          const visibleText = label.childElementCount ? `${text} ` : text;
          if (visibleTextNode.textContent !== visibleText) visibleTextNode.textContent = visibleText;
        } else if (label.childElementCount === 0) {
          label.textContent = text;
        }

        label.setAttribute('aria-label', text);
        label.lang = 'ru';
      }
    });

    const orderInput = this.shadowRoot.getElementById('custom#order_or_contract_id');
    if (!orderInput) return;

    orderInput.required = false;
    orderInput.removeAttribute('aria-required');
    orderInput.inputMode = 'numeric';
    orderInput.autocomplete = 'off';
    orderInput.minLength = 5;
    orderInput.maxLength = 5;
    orderInput.pattern = '[0-9]{5}';
    orderInput.placeholder = '12345';
    if (!this.orderInputs.has(orderInput)) {
      this.orderInputs.add(orderInput);
      orderInput.addEventListener('input', this.handleOrderInput);
      orderInput.addEventListener('blur', this.handleOrderInput);
    }

    const field = orderInput.parentElement;
    if (field) {
      field.classList.add('mostik-order-number-field');
      this.ensureOrderPrefix(field, orderInput);
      this.ensureOrderHelp(field, orderInput);
    }

    const form = orderInput.closest('form');
    if (form && !this.forms.has(form)) {
      this.forms.add(form);
      form.addEventListener('submit', this.handleSubmit, true);
    }

    this.validateOrderInput(orderInput);
  }

  ensureOrderPrefix(field, orderInput) {
    let prefix = field.querySelector('[data-mostik-order-prefix]');
    if (!prefix) {
      prefix = document.createElement('span');
      prefix.dataset.mostikOrderPrefix = '';
      prefix.setAttribute('aria-hidden', 'true');
      prefix.textContent = '#SP-';
      field.insertBefore(prefix, orderInput);
    }

    field.style.position = 'relative';
    prefix.style.position = 'absolute';
    prefix.style.insetInlineStart = '0.85rem';
    prefix.style.top = `${orderInput.offsetTop + orderInput.offsetHeight / 2}px`;
    prefix.style.transform = 'translateY(-50%)';
    prefix.style.pointerEvents = 'none';
    prefix.style.color = 'currentColor';
    prefix.style.zIndex = '1';

    const contentInset = prefix.offsetLeft + prefix.offsetWidth + 8;
    orderInput.style.paddingInlineStart = `${contentInset}px`;

    const orderLabel = this.shadowRoot.querySelector('label[for="custom#order_or_contract_id"]');
    if (orderLabel) orderLabel.style.insetInlineStart = `${contentInset}px`;
  }

  ensureOrderHelp(field, orderInput) {
    let help = field.querySelector('#mostik-order-number-help');
    if (!help) {
      help = document.createElement('p');
      help.id = 'mostik-order-number-help';
      help.dataset.mostikOrderHelp = '';
      field.appendChild(help);
    }

    const helpText = this.container.dataset.formOrderHelp || '';
    if (help.textContent !== helpText) help.textContent = helpText;
    help.lang = 'ru';
    help.style.margin = '0.45rem 0 0';
    help.style.fontSize = '0.875em';
    help.style.lineHeight = '1.45';
    help.style.opacity = '0.75';
    const describedBy = new Set(
      (orderInput.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean)
    );
    describedBy.add(help.id);
    orderInput.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
  }

  handleOrderInput(event) {
    const input = event.currentTarget;
    const digits = input.value.replace(/\D/g, '').slice(0, 5);

    if (input.value !== digits) input.value = digits;
    this.validateOrderInput(input);
  }

  validateOrderInput(input) {
    const isValid = input.value === '' || /^[0-9]{5}$/.test(input.value);
    input.setCustomValidity(isValid ? '' : this.container.dataset.formOrderError || '');
    return isValid;
  }

  handleSubmit(event) {
    const orderInput = this.shadowRoot && this.shadowRoot.getElementById('custom#order_or_contract_id');
    if (!orderInput || this.validateOrderInput(orderInput)) return;

    event.preventDefault();
    orderInput.reportValidity();
    orderInput.focus();
  }
}

function initialiseWithdrawalReceipt(page) {
  const receiptContainer = page.querySelector('[data-withdrawal-receipt]');
  const downloadLink = page.querySelector('[data-withdrawal-download]');
  const successMessage = page.querySelector('[data-withdrawal-success]');
  const receiptReadyMessage = page.querySelector('[data-withdrawal-receipt-ready]');
  const receiptUnavailableMessage = page.querySelector('[data-withdrawal-receipt-unavailable]');
  if (!receiptContainer || !downloadLink || !successMessage) return;

  let receipt;

  try {
    receipt = JSON.parse(sessionStorage.getItem('mostik:withdrawal-receipt'));
  } catch (error) {
    receipt = null;
  }

  if (!receipt && window.name.startsWith('mostik:withdrawal-receipt:')) {
    try {
      receipt = JSON.parse(window.name.replace('mostik:withdrawal-receipt:', ''));
    } catch (error) {
      receipt = null;
    }
    window.name = '';
  }

  if (!receipt) return;

  const receivedAtEpoch = Number(successMessage.dataset.withdrawalReceivedAtEpoch);
  const receivedAtDate = Number.isFinite(receivedAtEpoch)
    ? new Date(receivedAtEpoch * 1000)
    : new Date(receipt.submittedAtIso);
  const locale = document.documentElement.lang || 'de';

  receipt.receivedAt = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'long'
  }).format(receivedAtDate);
  receipt.receivedAtIso = receivedAtDate.toISOString();

  Object.entries(receipt).forEach(([key, value]) => {
    const target = page.querySelector(`[data-withdrawal-receipt-value="${key}"]`);
    if (target) target.textContent = value || '—';
  });

  const receiptTitle = page.dataset.receiptTitle || 'Withdrawal receipt';
  const fileName = page.dataset.receiptFilename || 'withdrawal-receipt.txt';
  const lines = [
    receiptTitle,
    '',
    `${page.dataset.receiptLabelStatement}:`,
    receipt.statement,
    '',
    `${page.dataset.receiptLabelName}: ${receipt.name}`,
    `${page.dataset.receiptLabelEmail}: ${receipt.email}`,
    `${page.dataset.receiptLabelOrder}: ${receipt.order}`,
    `${page.dataset.receiptLabelItems}: ${receipt.items}`,
    `${page.dataset.receiptLabelNotes}: ${receipt.notes || '—'}`,
    `${page.dataset.receiptLabelReceivedAt}: ${receipt.receivedAt} (${receipt.receivedAtIso})`
  ];

  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);

  downloadLink.href = objectUrl;
  downloadLink.download = fileName;
  downloadLink.hidden = false;
  receiptContainer.hidden = false;
  if (receiptReadyMessage) receiptReadyMessage.hidden = false;
  if (receiptUnavailableMessage) receiptUnavailableMessage.hidden = true;

  window.addEventListener('pagehide', () => URL.revokeObjectURL(objectUrl), { once: true });
}

document.querySelectorAll('[data-withdrawal-form]').forEach((form) => {
  new WithdrawalFormController(form).init();
});

document.querySelectorAll('[data-withdrawal-shopify-form]').forEach((container) => {
  new ShopifyWithdrawalFormController(container).init();
});

document.querySelectorAll('[data-withdrawal-page]').forEach((page) => {
  initialiseWithdrawalReceipt(page);
});
